import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowsClockwise, Plus, PencilSimple, Trash, CalendarBlank, CurrencyCircleDollar,
  CheckCircle, Clock, Pause, Play, X, Tag,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";
import { Card, Loading, EmptyState, SectionTitle, catColor } from "@/components/riq";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const CADENCES = [
  { k: "weekly", l: "Weekly" }, { k: "monthly", l: "Monthly" },
  { k: "quarterly", l: "Quarterly" }, { k: "yearly", l: "Yearly" },
];
const CADENCE_LABEL = Object.fromEntries(CADENCES.map((c) => [c.k, c.l]));
const CATEGORIES = [
  "Subscriptions", "Insurance", "Telecommunications", "Medical Aid", "Loans",
  "Home Loan", "Car Installment", "Rates", "Garden Services", "Pool Services",
  "Domestic Helper", "Entertainment", "Health & Beauty", "Transport", "Household",
  "Electronics", "Groceries", "Restaurants", "Fuel", "Clothing", "Other",
];

export default function Subscriptions() {
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // sub object or {} for new

  const load = () => api.get("/subscriptions").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this subscription? This can't be undone.")) return;
    try { await api.delete(`/subscriptions/${id}`); toast.success("Subscription removed"); load(); }
    catch { toast.error("Could not remove subscription"); }
  };
  const togglePause = async (s) => {
    try { await api.patch(`/subscriptions/${s.id}`, { active: !s.active }); toast.success(s.active ? "Paused" : "Resumed"); load(); }
    catch { toast.error("Could not update"); }
  };

  if (data === null) return <Loading label="Loading your subscriptions…" />;
  if (data === false) return <div className="text-muted-foreground">Could not load subscriptions.</div>;

  const { subscriptions, summary, upcoming, categories } = data;
  const maxCat = Math.max(1, ...categories.map((c) => c.monthly_cost));

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary flex items-center gap-2">
            <ArrowsClockwise size={26} weight="duotone" /> Subscriptions &amp; Debit Orders
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Track memberships, debit orders &amp; recurring expenses. Kept separate from your budget.</p>
        </div>
        <button data-testid="add-subscription-btn" onClick={() => setEditing({})}
          className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 font-medium hover:-translate-y-0.5 transition-transform">
          <Plus size={18} weight="bold" /> Add
        </button>
      </div>

      {subscriptions.length === 0 ? (
        <EmptyState icon={ArrowsClockwise} title="No subscriptions yet"
          subtitle="Add memberships like Netflix, your gym or insurance to see your monthly commitment and upcoming charges."
          action={<button data-testid="empty-add-subscription" onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full bg-riq-primary text-white px-6 py-3 font-medium"><Plus size={18} weight="bold" /> Add subscription</button>} />
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SumCard icon={CurrencyCircleDollar} label="Per month" value={money(summary.monthly_total, cur)} accent="#6E3FFC" testId="sum-monthly" />
            <SumCard icon={CalendarBlank} label="Per year" value={money(summary.yearly_total, cur)} accent="#2BB0C4" testId="sum-yearly" />
            <SumCard icon={ArrowsClockwise} label="Active" value={summary.count} accent="#42C97C" testId="sum-count" />
            <SumCard icon={Clock} label="Due soon" value={summary.due_soon} accent="#F5A623" testId="sum-due" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Upcoming */}
            <Card className="lg:col-span-2">
              <h3 className="font-head font-semibold text-riq-primary mb-3 flex items-center gap-2"><CalendarBlank size={18} weight="duotone" /> Next 30 days</h3>
              {upcoming.length ? (
                <div className="divide-y divide-riq-border">
                  {upcoming.map((s) => (
                    <div key={s.id} data-testid={`upcoming-${s.id}`} className="flex items-center gap-3 py-2.5">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: catColor(s.category) }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{s.name}</div>
                        <div className="text-xs text-muted-foreground">{shortDate(s.next_date)} · {CADENCE_LABEL[s.cadence]}</div>
                      </div>
                      <StatusPill status={s.status} days={s.days_until} />
                      <div className="font-head font-semibold w-24 text-right">{money(s.amount, cur)}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="py-6 text-center text-sm text-muted-foreground">Nothing due in the next 30 days.</div>}
            </Card>

            {/* Category breakdown */}
            <Card>
              <h3 className="font-head font-semibold text-riq-primary mb-3 flex items-center gap-2"><Tag size={18} weight="duotone" /> By category · monthly</h3>
              {categories.length ? (
                <div className="space-y-2.5">
                  {categories.map((c) => (
                    <div key={c.category} data-testid={`subcat-${c.category}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{c.category}</span>
                        <span className="text-muted-foreground text-xs">{money(c.monthly_cost, cur)}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(c.monthly_cost / maxCat) * 100}%`, background: catColor(c.category) }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="py-6 text-center text-sm text-muted-foreground">No active subscriptions.</div>}
            </Card>
          </div>

          {/* All subscriptions */}
          <div>
            <SectionTitle>All subscriptions</SectionTitle>
            <Card className="!p-0 divide-y divide-riq-border">
              {subscriptions.map((s) => (
                <div key={s.id} data-testid={`subscription-${s.id}`} className={`flex items-center gap-3 px-4 sm:px-5 py-3.5 ${!s.active ? "opacity-60" : ""}`}>
                  <div className="grid h-10 w-10 place-items-center rounded-xl shrink-0" style={{ background: `${catColor(s.category)}1a`, color: catColor(s.category) }}>
                    <ArrowsClockwise size={18} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {s.name}
                      {!s.active && <span className="rounded-full bg-neutral-200 text-neutral-600 px-1.5 py-0.5 text-[9px] font-bold uppercase">Paused</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {CADENCE_LABEL[s.cadence]} · {s.category} · next {shortDate(s.next_date)}
                      {s.payment_method ? ` · ${s.payment_method}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-head font-semibold">{money(s.amount, cur)}</div>
                    <div className="text-[11px] text-muted-foreground">≈ {money(s.monthly_cost, cur)}/mo</div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button data-testid={`sub-pause-${s.id}`} onClick={() => togglePause(s)} title={s.active ? "Pause" : "Resume"} className="p-1.5 rounded-lg hover:bg-secondary text-neutral-500">
                      {s.active ? <Pause size={16} weight="duotone" /> : <Play size={16} weight="duotone" />}
                    </button>
                    <button data-testid={`sub-edit-${s.id}`} onClick={() => setEditing(s)} title="Edit" className="p-1.5 rounded-lg hover:bg-secondary text-neutral-500 hover:text-riq-primary"><PencilSimple size={16} weight="duotone" /></button>
                    <button data-testid={`sub-delete-${s.id}`} onClick={() => remove(s.id)} title="Delete" className="p-1.5 rounded-lg hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={16} weight="duotone" /></button>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        </>
      )}

      <SubscriptionDialog editing={editing} cur={cur} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function SumCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="mb-2 grid h-8 w-8 place-items-center rounded-xl" style={{ background: `${accent}14`, color: accent }}><Icon size={17} weight="duotone" /></div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-2xl font-bold text-riq-primary mt-0.5 truncate">{value}</div>
    </Card>
  );
}

function StatusPill({ status, days }) {
  if (status === "due_soon") return <span className="rounded-full bg-riq-gold/25 text-[#B25E00] px-2.5 py-1 text-xs font-semibold flex items-center gap-1 shrink-0"><Clock size={12} weight="fill" /> {Math.max(days, 0)}d</span>;
  return <span className="rounded-full bg-riq-sage/70 text-riq-primary px-2.5 py-1 text-xs font-semibold flex items-center gap-1 shrink-0"><CalendarBlank size={12} weight="fill" /> {days}d</span>;
}

export function SubscriptionDialog({ editing, cur, onClose, onSaved }) {
  const open = !!editing;
  const isNew = editing && !editing.id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      name: editing.name || "",
      amount: editing.amount ?? "",
      cadence: editing.cadence || "monthly",
      next_billing_date: editing.next_billing_date || today,
      category: editing.category || "Subscriptions",
      payment_method: editing.payment_method || "",
      notes: editing.notes || "",
      active: editing.active ?? true,
    });
  }, [editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Please add a name."); return; }
    if (form.amount === "" || Number(form.amount) < 0) { toast.error("Please add a valid amount."); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (isNew) await api.post("/subscriptions", payload);
      else await api.patch(`/subscriptions/${editing.id}`, payload);
      toast.success(isNew ? "Subscription added" : "Subscription updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save subscription.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-riq-primary">{isNew ? "Add subscription" : "Edit subscription"}</DialogTitle>
          <DialogDescription className="sr-only">Recurring expense details</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Fld label="Name"><input data-testid="sub-field-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="riq-input w-full" placeholder="e.g. Netflix, Virgin Active" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label={`Amount (${cur})`}><input data-testid="sub-field-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="riq-input w-full" placeholder="0.00" /></Fld>
            <Fld label="Billing cycle">
              <select data-testid="sub-field-cadence" value={form.cadence} onChange={(e) => set("cadence", e.target.value)} className="riq-input w-full">
                {CADENCES.map((c) => <option key={c.k} value={c.k}>{c.l}</option>)}
              </select>
            </Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Next billing date"><input data-testid="sub-field-date" type="date" value={form.next_billing_date} onChange={(e) => set("next_billing_date", e.target.value)} className="riq-input w-full" /></Fld>
            <Fld label="Category">
              <select data-testid="sub-field-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="riq-input w-full">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Fld>
          </div>
          <Fld label="Payment method (optional)"><input data-testid="sub-field-payment" value={form.payment_method || ""} onChange={(e) => set("payment_method", e.target.value)} className="riq-input w-full" placeholder="e.g. Visa ••4521" /></Fld>
          <Fld label="Notes (optional)"><textarea data-testid="sub-field-notes" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="riq-input w-full resize-none" /></Fld>
          <div className="flex gap-3 pt-1">
            <button data-testid="sub-cancel" onClick={onClose} className="flex-1 rounded-full border border-riq-border py-2.5 text-sm font-medium">Cancel</button>
            <button data-testid="sub-save" onClick={save} disabled={saving} className="flex-1 rounded-full bg-riq-primary text-white py-2.5 text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}
